if (!customElements.get("store-availability")) {
  class StoreAvailability extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.variantId = this.dataset.variantId;
      this.apiUrl = this.dataset.apiUrl;
      this.iconAvailable = this.dataset.iconAvailable;
      this.iconUnavailable = this.dataset.iconUnavailable;
      this.textAvailable = this.dataset.textAvailable;
      this.textUnavailable = this.dataset.textUnavailable;
      this.inStockTemplate = this.dataset.inStockTemplate;
      this.inStockAvailableTemplate = this.dataset.inStockAvailableTemplate;
      this.locations = [];
      this.locationDetails = this.parseLocationDetails();
      this.directionsByStore = this.parseDirections();
      this.deliveryIcon = this.querySelector("[data-delivery-icon]");
      this.deliveryText = this.querySelector("[data-delivery-text]");
      this.storesColumn = this.querySelector("[data-stores]");
      this.list = this.querySelector("[data-locations]");
      this.itemTemplate = this.querySelector("[data-location-template]");
      this.drawer = this.querySelector("[data-drawer]");
      this.drawerOverlay = this.querySelector("[data-drawer-overlay]");
      this.drawerPanel = this.querySelector("[data-drawer-panel]");
      this.drawerClose = this.querySelector("[data-drawer-close]");
      this.drawerName = this.querySelector("[data-drawer-name]");
      this.drawerIcon = this.querySelector("[data-drawer-icon]");
      this.drawerStock = this.querySelector("[data-drawer-stock]");
      this.drawerAddressSection = this.querySelector("[data-drawer-address-section]");
      this.drawerAddress = this.querySelector("[data-drawer-address]");
      this.drawerDirections = this.querySelector("[data-drawer-directions]");
      this.drawerHoursSection = this.querySelector("[data-drawer-hours-section]");
      this.drawerHours = this.querySelector("[data-drawer-hours]");
      this.drawerStoreLocator = this.querySelector("[data-drawer-store-locator]");
      this.onKeyDown = this.onKeyDown.bind(this);

      if (this.drawer && this.drawer.parentElement !== document.body) {
        document.body.appendChild(this.drawer);
      }

      this.bindDrawerEvents();
      this.updateDelivery(this.dataset.variantAvailable === "true");
      this.handleVariantChange();
      this.fetchInventory();
    }

    disconnectedCallback() {
      document.removeEventListener("keydown", this.onKeyDown);
      document.body.classList.remove("store-availability-drawer-open");

      if (this.drawer && this.drawer.parentElement === document.body) {
        this.drawer.remove();
      }
    }

    parseLocationDetails() {
      const script = this.querySelector("[data-location-details]");
      if (!script) return [];

      try {
        const data = JSON.parse(script.textContent);
        return Array.isArray(data) ? data : [];
      } catch (error) {
        return [];
      }
    }

    parseDirections() {
      const script = this.querySelector("[data-store-directions]");
      if (!script) return {};

      try {
        const data = JSON.parse(script.textContent);
        return data && typeof data === "object" ? data : {};
      } catch (error) {
        return {};
      }
    }

    findDirectionsUrl(locationName) {
      const key = this.normalizeName(locationName);
      if (!key) return "";

      if (this.directionsByStore[key]) return this.directionsByStore[key];

      const matchKey = Object.keys(this.directionsByStore).find((storeKey) => {
        return key.includes(storeKey) || storeKey.includes(key);
      });

      return matchKey ? this.directionsByStore[matchKey] : "";
    }

    bindDrawerEvents() {
      if (this.drawerClose) {
        this.drawerClose.addEventListener("click", () => this.closeDrawer());
      }

      if (this.drawerOverlay) {
        this.drawerOverlay.addEventListener("click", () => this.closeDrawer());
      }
    }

    handleVariantChange() {
      if (!window.MinimogEvents || !this.productId) return;

      window.MinimogEvents.subscribe(`${this.productId}__VARIANT_CHANGE`, (variant) => {
        if (variant) {
          this.variantId = String(variant.id);
          this.updateDelivery(Boolean(variant.available));
        } else {
          this.variantId = "";
          this.updateDelivery(false);
        }

        this.renderLocations();
      });
    }

    numericId(value) {
      if (!value) return "";
      const asString = String(value);
      return asString.includes("/") ? asString.split("/").pop() : asString;
    }

    normalizeName(value) {
      return String(value || "")
        .trim()
        .toLowerCase();
    }

    findLocationDetails(locationName) {
      const key = this.normalizeName(locationName);
      return this.locationDetails.find((item) => this.normalizeName(item.name) === key) || null;
    }

    async fetchInventory() {
      if (!this.apiUrl || !this.productId) return;

      const separator = this.apiUrl.includes("?") ? "&" : "?";
      const url = `${this.apiUrl}${separator}productId=${encodeURIComponent(this.productId)}`;

      try {
        const response = await fetch(url);

        if (!response.ok) throw new Error("Inventory request failed");

        const data = await response.json();
        this.locations = Array.isArray(data.locations) ? data.locations : [];
        this.renderLocations();
      } catch (error) {
        this.locations = [];
        this.renderLocations();
      }
    }

    getLocationQuantity(location) {
      const variants = Array.isArray(location.variants) ? location.variants : [];

      if (this.variantId) {
        const match = variants.find((variant) => this.numericId(variant.variantId) === this.numericId(this.variantId));
        if (match) return Number(match.available) || 0;
      }

      return Number(location.available) || 0;
    }

    updateDelivery(available) {
      if (!this.deliveryIcon || !this.deliveryText) return;

      this.deliveryIcon.src = available ? this.iconAvailable : this.iconUnavailable;
      this.deliveryText.textContent = available ? this.textAvailable : this.textUnavailable;
    }

    renderLocations() {
      if (!this.list || !this.itemTemplate) return;

      this.list.replaceChildren();

      if (!this.locations.length) {
        if (this.storesColumn) this.storesColumn.hidden = true;
        this.removeAttribute("aria-busy");
        return;
      }

      if (this.storesColumn) this.storesColumn.hidden = false;

      this.locations.forEach((location) => {
        const quantity = this.getLocationQuantity(location);
        const available = quantity > 0;
        const node = this.itemTemplate.content.firstElementChild.cloneNode(true);
        const button = node.querySelector("[data-location-btn]");
        const icon = node.querySelector("[data-icon]");
        const name = node.querySelector("[data-name]");
        const stock = node.querySelector("[data-stock]");
        const locationName = location.locationName || "";

        if (icon) {
          icon.src = available ? this.iconAvailable : this.iconUnavailable;
        }

        if (name) name.textContent = locationName;

        if (stock) {
          stock.textContent = available
            ? this.inStockTemplate.replace("COUNT", String(quantity))
            : this.textUnavailable;
        }

        if (button) {
          button.addEventListener("click", () => {
            this.openDrawer({
              name: locationName,
              quantity,
              available,
              details: this.findLocationDetails(locationName),
            });
          });
        }

        this.list.appendChild(node);
      });

      this.removeAttribute("aria-busy");
    }

    openDrawer({ name, quantity, available, details }) {
      if (!this.drawer) return;

      if (this.drawerName) this.drawerName.textContent = name || "";

      if (this.drawerIcon) {
        this.drawerIcon.src = available ? this.iconAvailable : this.iconUnavailable;
      }

      if (this.drawerStock) {
        this.drawerStock.textContent = available
          ? this.inStockAvailableTemplate.replace("COUNT", String(quantity))
          : this.textUnavailable;
      }

      const address = details && details.address ? String(details.address).trim() : "";
      const hours = details && details.hours ? String(details.hours).trim() : "";
      const directionsUrl = this.findDirectionsUrl(name);

      if (this.drawerAddressSection && this.drawerAddress) {
        if (address) {
          this.drawerAddress.innerHTML = address;
        } else {
          this.drawerAddress.innerHTML = "";
        }

        // Keep address section visible whenever we have address content or a directions link.
        this.drawerAddressSection.hidden = !(address || directionsUrl);
      }

      if (this.drawerDirections) {
        if (directionsUrl) {
          this.drawerDirections.href = directionsUrl;
          this.drawerDirections.removeAttribute("hidden");
          this.drawerDirections.hidden = false;
          this.drawerDirections.style.display = "";
        } else {
          this.drawerDirections.setAttribute("hidden", "");
          this.drawerDirections.hidden = true;
        }
      }

      if (this.drawerHoursSection && this.drawerHours) {
        if (hours) {
          this.drawerHours.innerHTML = hours;
          this.drawerHoursSection.hidden = false;
        } else {
          this.drawerHours.innerHTML = "";
          this.drawerHoursSection.hidden = true;
        }
      }

      if (this.drawerStoreLocator) {
        this.drawerStoreLocator.removeAttribute("hidden");
        this.drawerStoreLocator.hidden = false;
        this.drawerStoreLocator.style.display = "";
      }

      this.drawer.hidden = false;
      requestAnimationFrame(() => {
        this.drawer.classList.add("is-open");
      });
      document.body.classList.add("store-availability-drawer-open");
      document.addEventListener("keydown", this.onKeyDown);

      if (this.drawerPanel) this.drawerPanel.focus();
    }

    closeDrawer() {
      if (!this.drawer || this.drawer.hidden) return;

      this.drawer.classList.remove("is-open");
      document.body.classList.remove("store-availability-drawer-open");
      document.removeEventListener("keydown", this.onKeyDown);

      window.setTimeout(() => {
        if (!this.drawer.classList.contains("is-open")) {
          this.drawer.hidden = true;
        }
      }, 250);
    }

    onKeyDown(event) {
      if (event.key === "Escape") this.closeDrawer();
    }
  }

  customElements.define("store-availability", StoreAvailability);
}
